/**
 * The typed client for the srank API.
 *
 * Everything goes through the Rust edge on the same origin, so the session cookie
 * rides along automatically and there is no token to store in JS (nothing for an XSS
 * to steal). A 401 anywhere means the session is gone; callers surface that as
 * "logged out" rather than as a data error.
 */

export class Unauthorized extends Error {}

export async function get<T>(path: string, params?: Record<string, string | number | boolean>): Promise<T> {
  const qs = params
    ? "?" + new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()
    : "";
  const res = await fetch(`/api/v1${path}${qs}`, { credentials: "include" });
  if (res.status === 401) throw new Unauthorized("session expired");
  if (!res.ok) throw new Error(`${path}: ${res.status} ${await res.text()}`);
  return res.json() as Promise<T>;
}

export interface RankRow {
  rank: number;
  cik: number;
  ticker: string;
  title: string;
  raw_value: number | null;
  zscore: number | null;
}

export interface RankResponse {
  on: string;
  factor: string;
  total: number;
  /** Whether this came from the parquet cache. The UI shows it — a cold recompute
   *  taking seconds is honest; pretending everything is instant is not. */
  cached: boolean;
  elapsed_ms: number;
  rows: RankRow[];
}

export interface CompositeRow {
  rank: number;
  cik: number;
  ticker: string;
  title: string;
  composite: number | null;
  n_factors: number;
}

export interface FactorMeta {
  name: string;
  family: string;
  /** +1 = higher is better, -1 = LOWER is better. The UI must respect this or it
   *  repeats the predecessor's sign bug in the presentation layer. */
  direction: number;
  description: string;
  needs_prices: boolean;
}

/** The measured record of a buy-list recipe. Never rendered without the names, and
 *  the names are never rendered without it — the same coupling the API enforces. */
export interface RecipeEvidence {
  window: string;
  n_periods: number;
  /** The RANKING metric — does it order stocks correctly. */
  ic_t: number;
  /** The MONEY metric — net-of-cost tilt vs the equal-weight universe. These two
   *  disagree often enough that showing either alone misleads, so the strip shows
   *  both, always. */
  excess_t: number;
  excess_monthly: number;
  excess_annual: number;
  cagr: number;
  bench_cagr: number;
  max_drawdown: number;
  turnover: number;
  /** True = the edge is stock selection (survives sector+size neutralization).
   *  False = it is an allocation bet wearing a stock-picker's coat. */
  survives_neutralization: boolean;
  what_it_actually_is: string;
  /** False for every recipe currently shipped — nothing clears sqrt(2 ln N) for the
   *  N configurations tried. The strip states this rather than implying significance. */
  beats_luck_benchmark: boolean;
  luck_benchmark_t: number;
  configurations_tried: number;
}

export interface BuyListResponse {
  on: string;
  recipe: string;
  description: string;
  n_investable: number;
  elapsed_ms: number;
  evidence: RecipeEvidence;
  rows: RankRow[];
}

export interface RecipeSummary {
  name: string;
  factor: string;
  description: string;
  cagr: number;
  bench_cagr: number;
  ic_t: number;
  excess_t: number;
  survives_neutralization: boolean;
  what_it_actually_is: string;
  beats_luck_benchmark: boolean;
}

export interface RecipesResponse {
  default: string;
  /** The most recent month-end the data covers and the warm has cached — the date the
   *  UI should open on so it serves a fresh list, not a stale hardcoded one. */
  latest_month_end: string | null;
  /** The newest PRICE BAR held — how current the inputs are, as opposed to which
   *  cross-section the list describes. Price acquisition ended with the premium
   *  subscription, so this is frozen and the gap to today grows daily. The UI must
   *  surface it: a list off a frozen feed reads as current unless it says otherwise. */
  prices_through: string | null;
  luck_benchmark_t: number;
  configurations_tried: number;
  /** FF12 industries available as a sub-universe filter. */
  sectors: string[];
  recipes: RecipeSummary[];
}

export interface Bar {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  adj_close?: number;
}

export const api = {
  health: () => get<Record<string, unknown>>("/health"),
  factors: () => get<{ factors: FactorMeta[]; composites: Record<string, string[]> }>("/factors"),
  rank: (on: string, factor: string, opts: { limit?: number; excludeFinancials?: boolean } = {}) =>
    get<RankResponse>("/rank", {
      on,
      factor,
      limit: opts.limit ?? 250,
      exclude_financials: opts.excludeFinancials ?? false,
    }),
  recipes: () => get<RecipesResponse>("/recipes"),
  buylist: (
    on: string,
    recipe: string,
    opts: { limit?: number; exchange?: string; sector?: string; excludeFinancials?: boolean } = {},
  ) =>
    get<BuyListResponse>("/buylist", {
      on,
      recipe,
      limit: opts.limit ?? 300,
      ...(opts.exchange ? { exchange: opts.exchange } : {}),
      ...(opts.sector ? { sector: opts.sector } : {}),
      exclude_financials: opts.excludeFinancials ?? false,
    }),
  composite: (on: string, bundle: string, opts: { sector?: boolean; size?: boolean } = {}) =>
    get<{ rows: CompositeRow[]; total: number; factors: string[] }>("/composite", {
      on,
      bundle,
      neutralize_sector: opts.sector ?? false,
      neutralize_size: opts.size ?? false,
    }),
  company: (cik: number, on: string) =>
    get<{ cik: number; on: string; concepts: Array<Record<string, unknown>> }>(
      `/company/${cik}`,
      { on },
    ),
  prices: (ticker: string, start?: string, end?: string) =>
    get<{ ticker: string; bars: Bar[]; actions: Array<Record<string, unknown>> }>(
      `/prices/${ticker}`,
      { ...(start ? { start } : {}), ...(end ? { end } : {}) },
    ),
  universe: (on: string) =>
    get<{ on: string; eligible: number; since_delisted: number; since_delisted_pct: number }>(
      "/universe",
      { on },
    ),
};

/** Auth lives on the edge, outside the versioned data API. */
export const auth = {
  async me(): Promise<{ user: string } | null> {
    const res = await fetch("/api/auth/me", { credentials: "include" });
    return res.ok ? ((await res.json()) as { user: string }) : null;
  },
  async login(username: string, password: string): Promise<boolean> {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    return res.ok;
  },
  async logout(): Promise<void> {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
  },
};
